/**
 * Off-DOM `<video>` pre-warmer and slot-swap pool.
 *
 * Why a pool of `<video>` elements instead of `<link rel="prefetch">` or `fetch()` alone:
 *   1. Match working copies have a 2.6-3.6 MB `moov` atom and 5-second GOPs (~150 frames).
 *   2. Mutating `v.src = "...#t=start,end"` on a single `<video>` tears down Chromium's
 *      `WebMediaPlayerImpl`, re-parses the sample table, fetches the keyframe range, and
 *      decodes up to 150 frames from the preceding keyframe to `start` (~470-700 ms).
 *   3. A hidden `<video preload="auto" muted playsInline>` in `document.body` (outside `#root`
 *      so E2E/DOM selectors inside the app never see standby nodes) performs the redirect,
 *      byte-range fetch, `#t=start` seek, and GOP frame decode in the background (`readyState === 4`)
 *      while the user watches the current clip. Swapping that warm `<video>` into the player
 *      container starts playback in ~30 ms.
 */

const MAX_POOL = 4;

interface PoolEntry {
  src: string;
  el: HTMLVideoElement;
}

const pool: PoolEntry[] = [];
const activeEls = new Set<HTMLVideoElement>();
let container: HTMLDivElement | null = null;

function poolContainer(): HTMLDivElement | null {
  if (typeof document === "undefined") return null;
  if (container && container.isConnected) return container;
  container = document.createElement("div");
  container.setAttribute("data-clip-prefetch-pool", "1");
  container.setAttribute("aria-hidden", "true");
  container.style.cssText = "position:fixed;left:-9999px;top:-9999px;width:1px;height:1px;overflow:hidden;opacity:0;pointer-events:none;display:none";
  document.body.appendChild(container);
  return container;
}

function destroyVideo(el: HTMLVideoElement): void {
  try {
    el.pause();
    el.removeAttribute("src");
    el.load();
    el.remove();
  } catch {
    // ignore cleanup errors in headless/unmounted environments
  }
}

function evictIfNeeded(): void {
  while (pool.length > MAX_POOL) {
    const oldest = pool.shift();
    if (oldest) destroyVideo(oldest.el);
  }
}

function isUrlActive(src: string): boolean {
  for (const el of activeEls) {
    if (el.dataset.clipSrc === src) return true;
  }
  return false;
}

/**
 * Pre-warms up to `MAX_POOL` clip playback URLs in priority order (first URL = highest priority).
 */
export function prefetchClipUrls(urls: readonly (string | null | undefined)[]): void {
  const box = poolContainer();
  if (!box) return;
  const unique: string[] = [];
  for (const u of urls) {
    if (!u || unique.includes(u) || isUrlActive(u)) continue;
    unique.push(u);
    if (unique.length >= MAX_POOL) break;
  }
  // Insert in reverse order so `unique[0]` (highest priority) is at the MRU end of `pool`.
  for (let i = unique.length - 1; i >= 0; i--) {
    const src = unique[i]!;
    const idx = pool.findIndex((p) => p.src === src);
    if (idx !== -1) {
      const [hit] = pool.splice(idx, 1);
      if (hit && !hit.el.error) {
        pool.push(hit);
        continue;
      }
      if (hit) destroyVideo(hit.el);
    }
    const el = document.createElement("video");
    el.muted = true;
    el.playsInline = true;
    el.preload = "auto";
    el.dataset.clipSrc = src;
    el.dataset.played = "0";
    el.src = src;
    box.appendChild(el);
    pool.push({ src, el });
    evictIfNeeded();
  }
}

/**
 * Acquires a warm `<video>` for `src` from the pool, or creates a fresh one if not pooled.
 */
export function acquireWarmVideo(src: string): HTMLVideoElement {
  const idx = pool.findIndex((p) => p.src === src);
  if (idx !== -1) {
    const [hit] = pool.splice(idx, 1);
    if (hit && !hit.el.error) {
      hit.el.dataset.played = "1";
      activeEls.add(hit.el);
      return hit.el;
    }
    if (hit) destroyVideo(hit.el);
  }
  const el = document.createElement("video");
  el.muted = true;
  el.playsInline = true;
  el.preload = "auto";
  el.dataset.clipSrc = src;
  el.dataset.played = "1";
  el.src = src;
  activeEls.add(el);
  return el;
}

/**
 * Releases a `<video>` back to the hidden pool when a player switches clips or unmounts.
 * Reloads the element in the background if it was played so its `#t=start,end` media
 * fragment end-pause is re-armed for future playback.
 */
export function releaseVideoToPool(el: HTMLVideoElement): void {
  activeEls.delete(el);
  const src = el.dataset.clipSrc;
  const box = poolContainer();
  if (!src || !box || el.error || pool.some((p) => p.src === src)) {
    destroyVideo(el);
    return;
  }
  try {
    el.pause();
    el.removeAttribute("poster");
    el.removeAttribute("controlsList");
    el.removeAttribute("aria-label");
    box.appendChild(el);
    if (el.dataset.played === "1") {
      el.dataset.played = "0";
      el.load();
    }
    pool.push({ src, el });
    evictIfNeeded();
  } catch {
    destroyVideo(el);
  }
}
